import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ChatCircleText, EnvelopeSimple, Globe, Megaphone, PresentationChart, QrCode, ShareNetwork, ThumbsUp, UsersThree } from '@phosphor-icons/react';
import { Slide } from '../Slide';
import { InView } from '../InView';
import { SlideHeadline } from '../SlideHeadline';
import { EmbeddedSnakWindow } from '../EmbeddedSnakWindow';
import { congressBoothImage, nextSteps, screenshots, shareChannels, snakkerSupport } from '../../../data/deck';
import type { ChannelId, ShareChannel } from '../../../data/deck';
export function ChannelsSlide() {
  const [congressOpen, setCongressOpen] = useState(false);
  return <Slide id="channels" data-id="element-167" data-mp="20cw6akqnnq6f">
      <AnimatePresence data-id="element-168" data-mp="20bkr7orrew00">
        {congressOpen && <EmbeddedSnakWindow key="congress" url={congressBoothImage} title="Congress activation · example booth" shape="browser" content={<div className="flex h-full w-full items-center justify-center bg-white" data-id="element-170" data-mp="1ws0ob0kt8hiq">
                <img src={congressBoothImage} alt="Congress activation: a Snak screen on a phone beside the matching congress booth it was designed for" className="block max-h-full max-w-full object-contain" data-id="element-171" data-mp="21qvb0dlweq84" />
              </div>} onClose={() => setCongressOpen(false)} data-id="element-169" data-mp="3gr5lao90c2kl" />}
      </AnimatePresence>
      <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16" data-id="element-172" data-mp="1j0421d5tp1u9">
        <InView data-id="element-173" data-mp="1nzgq218fkeyf">
          <SlideHeadline lead="One experience." follow="More channels to share." data-id="element-174" data-mp="1nyx50bnhhag6" />
        </InView>
        <InView delay={0.1} data-id="element-175" data-mp="1sqp111pes8dp">
          <p className="font-body text-[14.5px] leading-[1.7] text-[rgba(1,51,43,0.65)]" data-id="element-176" data-mp="1sqypulypw9ht">
            A single Snak can meet dentists wherever attention already exists — without
            rebuilding the experience for every channel.
          </p>
        </InView>
      </div>

      <div className="mt-14 grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-10" data-id="element-177" data-mp="s8eo7p32ebyr">
        <InView delay={0.15} y={26} data-id="element-178" data-mp="navacqaa417c">
          <figure className="flex h-full flex-col rounded-[30px] bg-background p-6 sm:p-7" data-id="element-179" data-mp="n9tpju3go0cf">
            <div className="flex items-center gap-4" data-id="element-180" data-mp="11ygb4djoloc3">
              <span className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-white/[0.08] text-primary" data-id="element-181" data-mp="wzd971yzzy8c">
                <Globe size={22} aria-hidden="true" data-id="element-182" data-mp="9bdhnztv43sp" />
              </span>
              <div data-id="element-183" data-mp="4br5ltprstlh">
                <span className="block font-body text-[10px] uppercase tracking-[0.22em] text-primary" data-id="element-184" data-mp="4caphfyzds25">
                  Example entry point
                </span>
                <span className="mt-1 block font-heading text-[24px] leading-none text-white" data-id="element-185" data-mp="e9f48uqhyl8x">
                  The Filtek Snak
                </span>
              </div>
            </div>
            <div className="mt-6 flex flex-1 items-center justify-center rounded-[22px] bg-[#F4F2EC] px-6 py-8" data-id="element-186" data-mp="1uphuyrybk81y">
              <img src={screenshots.supremeSnak} alt="The Filtek Snak on a phone, introducing Filtek Supreme as precise, detail-focused and built for aesthetic control" className="block h-[min(58vh,560px)] min-h-[380px] w-auto mix-blend-multiply" data-id="element-187" data-mp="1x8cy98osb6vm" />
            </div>
          </figure>
        </InView>

        <div className="flex flex-col" data-id="element-188" data-mp="1s9a3ch2iqh1d">
          <InView delay={0.2} data-id="element-189" data-mp="2fyukcuf43480">
            <div className="flex items-end justify-between gap-6" data-id="element-190" data-mp="2fzw0hdazigwl">
              <div data-id="element-191" data-mp="2ky795kpjmld8">
                <span className="font-body text-[10px] uppercase tracking-[0.22em] text-[rgba(1,51,43,0.45)]" data-id="element-192" data-mp="2kwmdfrrhswds">
                  The same Snak
                </span>
                <h3 className="mt-2 font-heading text-[28px] leading-[1.08] tracking-[-0.02em] text-background sm:text-[34px]" data-id="element-193" data-mp="2b7me2792vyz2">
                  Ready for every relevant touchpoint
                </h3>
              </div>
              <span className="hidden shrink-0 rounded-full bg-background px-3.5 py-1.5 font-body text-[10px] uppercase tracking-[0.16em] text-primary sm:inline-flex" data-id="element-194" data-mp="33of7lpiasnn3">
                One build · many entries
              </span>
            </div>
          </InView>

          <ul className="mt-7 grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-195" data-mp="161o4xiq23fxn">
            {shareChannels.map((channel, i) => <InView key={channel.id} delay={0.24 + i * 0.04} className="h-full" data-id="element-196" data-mp="120uh0pcm614r" data-mpi={i}>
                <li className="h-full" data-id="element-197" data-mp="1yq22xyh5ac97">
                  {channel.id === 'congress' ? <button type="button" onClick={() => setCongressOpen(true)} aria-label="Congress — view an example booth" className="group relative flex h-full min-h-[170px] w-full flex-col justify-between rounded-[20px] bg-white p-5 text-left ring-1 ring-[rgba(1,51,43,0.08)] transition-shadow hover:ring-2 hover:ring-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-id="element-198" data-mp="1tr8s6dt8szcq">
                      <ChannelTileBody channel={channel} data-id="element-199" data-mp="3vd4fcy4ve3dq" />
                      <span className="absolute right-4 top-4 rounded-full bg-background px-2.5 py-1 font-body text-[9.5px] uppercase tracking-[0.14em] text-primary" data-id="element-200" data-mp="3vepaaov3joc4">
                        View
                      </span>
                    </button> : <div className="flex h-full min-h-[170px] flex-col justify-between rounded-[20px] bg-white p-5 ring-1 ring-[rgba(1,51,43,0.08)]" data-id="element-201" data-mp="d8138tghkr3t">
                      <ChannelTileBody channel={channel} data-id="element-202" data-mp="n5fgkk744zrw" />
                    </div>}
                </li>
              </InView>)}
          </ul>

          <InView delay={0.5} data-id="element-203" data-mp="ry8n9dm56i6m">
            <p className="mt-3 flex items-center gap-4 rounded-[20px] bg-white px-5 py-5 font-heading text-[18px] leading-snug text-background ring-1 ring-[rgba(1,51,43,0.08)] sm:text-[20px]" data-id="element-204" data-mp="vxhhejl3gi2p">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-background" data-id="element-205" data-mp="15vnhqwr95g5g">
                <ShareNetwork size={18} weight="bold" aria-hidden="true" data-id="element-206" data-mp="31ya0uhefdrr7" />
              </span>
              Consistent experience, consistent measurement — regardless of where the journey
              begins.
            </p>
          </InView>
        </div>
      </div>

      <InView delay={0.2} data-id="element-207" data-mp="36y4rqky7ahc9">
        <div className="mt-14 grid gap-8 border-t border-[rgba(1,51,43,0.12)] pt-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-end lg:gap-16" data-id="element-208" data-mp="36xv619k6uwcd">
          <p className="max-w-[26ch] font-heading text-[28px] leading-[1.08] tracking-[-0.02em] text-background sm:text-[36px]" data-id="element-209" data-mp="3krdzkh6i2uso">
            Creative consistency at regional level.{' '}
            <span className="text-[rgba(1,51,43,0.45)]" data-id="element-210" data-mp="3ulmqx1jsohp9">Relevant activation at local level.</span>
          </p>
          <div data-id="element-211" data-mp="3pmtcn6woomo1">
            <span className="font-body text-[10px] uppercase tracking-[0.2em] text-[rgba(1,51,43,0.45)]" data-id="element-212" data-mp="209g3939ps8g2">
              Snakker supports local brand teams with
            </span>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-213" data-mp="1vcy20ddles8h">
              {snakkerSupport.map(item => <span key={item} className="rounded-full border border-[rgba(1,51,43,0.18)] px-3 py-1 font-body text-[12px] text-background" data-id="element-214" data-mp="aux11m6wi51x">
                  {item}
                </span>)}
            </div>
            <p className="mt-3 font-body text-[12px] text-[rgba(1,51,43,0.55)]" data-id="element-215" data-mp="6017ghm0n9mp">
              Practical support — not a new platform or an extra layer.
            </p>
          </div>
        </div>
      </InView>

      <InView delay={0.26} data-id="element-216" data-mp="3t4h1qtqbt4sv">
        <div className="mt-10 rounded-[24px] bg-background p-6 sm:p-8" data-id="element-217" data-mp="3e9j87xjfy9a7">
          <span className="font-body text-[10px] uppercase tracking-[0.22em] text-primary" data-id="element-218" data-mp="3ebnlh638ckn5">
            Suggested next step
          </span>
          <ol className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-219" data-mp="1choaklu0s5az">
            {nextSteps.map((step, i) => <li key={step.verb} className="border-t border-white/15 pt-4" data-id="element-220" data-mp="1hfpu17vcy6mz" data-mpi={i}>
                <span className="font-body text-[10px] tabular-nums text-white/40" data-id="element-221" data-mp="57xe3bmnvn06">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p className="mt-1 font-heading text-[18px] leading-snug text-white" data-id="element-222" data-mp="anptzb0dhdx">
                  <span className="text-primary" data-id="element-223" data-mp="3rle53adivhvh">{step.verb}</span> {step.text}
                </p>
              </li>)}
          </ol>
        </div>
      </InView>
    </Slide>;
}
function ChannelTileBody({
  channel
}: {
  channel: ShareChannel;
}) {
  return <>
      <span className="flex h-11 w-11 items-center justify-center rounded-[12px] bg-[#F4F2EC] text-background" data-id="element-224" data-mp="3dmo6treyvc5j">
        <ChannelGlyph id={channel.id} data-id="element-225" data-mp="38owbenf457wl" />
      </span>
      <span className="mt-6" data-id="element-226" data-mp="33pt8b8ip1br9">
        <span className="block font-heading text-[17px] leading-tight text-background" data-id="element-227" data-mp="33ql3ii8dp7h0">
          {channel.name}
        </span>
        <span className="mt-1 block font-body text-[12px] leading-snug text-[rgba(1,51,43,0.55)]" data-id="element-228" data-mp="16ux3mc1798on">
          {channel.detail}
        </span>
      </span>
    </>;
}
function ChannelGlyph({
  id
}: {
  id: ChannelId;
}) {
  const size = 20;
  if (id === 'embed') return <Globe size={size} aria-hidden="true" data-id="element-229" data-mp="1efh5h8xpaspi" />;
  if (id === 'messaging') return <ChatCircleText size={size} aria-hidden="true" data-id="element-230" data-mp="1j7irhmko7xsq" />;
  if (id === 'detailer') return <PresentationChart size={size} aria-hidden="true" data-id="element-231" data-mp="1j9lfy266divc" />;
  if (id === 'qr') return <QrCode size={size} aria-hidden="true" data-id="element-232" data-mp="1o5kay8m0dz4q" />;
  if (id === 'post') return <ThumbsUp size={size} aria-hidden="true" data-id="element-233" data-mp="qjmsgb7mptss" />;
  if (id === 'email') return <EnvelopeSimple size={size} aria-hidden="true" data-id="element-234" data-mp="vh4udhn8b8ra" />;
  if (id === 'advertise') return <Megaphone size={size} aria-hidden="true" data-id="element-235" data-mp="10gr9jwmmaz2q" />;
  return <UsersThree size={size} aria-hidden="true" data-id="element-236" data-mp="10fy6p6qs4tpv" />;
}